import React, { useState, useEffect } from "react";
import { MdPowerSettingsNew } from "react-icons/md";
import MyAccount from "./Header/MyAccount";
import logo from "../assets/images/logo.svg";
import "../index.scss";
import Context from "../context";

type Mode = "login" | "register" | "forgot" | "reset";

function AuthModal() {
  const { userInfo, updateUserInfo } = React.useContext(Context);
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState<Mode>("login");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [nickname, setNickname] = useState("");
  const [nicknameStatus, setNicknameStatus] = useState<"idle"|"checking"|"available"|"taken">("idle");
  const [pin, setPin] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const apiUrl = "";

  useEffect(() => {
    const savedPhone = localStorage.getItem("aviator-saved-phone");
    const savedPassword = localStorage.getItem("aviator-saved-password");
    if (savedPhone && savedPassword) {
      setPhone(savedPhone);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (!nickname || nickname.length < 3) { setNicknameStatus("idle"); return; }
    setNicknameStatus("checking");
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${apiUrl}/api/check-nickname/${nickname}`);
        const data = await res.json();
        setNicknameStatus(data.available ? "available" : "taken");
      } catch { setNicknameStatus("idle"); }
    }, 500);
    return () => clearTimeout(timer);
  }, [nickname]);

  const resetForm = () => {
    setPhone(""); setPassword(""); setConfirmPassword("");
    setNickname(""); setPin(""); setNewPassword("");
    setError(""); setSuccess(""); setNicknameStatus("idle");
  };

  const handleSubmit = async () => {
    setError(""); setSuccess("");
    if (mode === "register") {
      if (!phone || !password || !confirmPassword) { setError("All fields are required"); return; }
      if (password !== confirmPassword) { setError("Passwords do not match"); return; }
      if (password.length < 4) { setError("Password must be at least 4 characters"); return; }
      if (nickname && nicknameStatus === "taken") { setError("Nickname already taken, choose another"); return; }
    } else if (mode === "login") {
      if (!phone || !password) { setError("Phone and password required"); return; }
    } else if (mode === "forgot") {
      if (!phone) { setError("Phone number required"); return; }
    } else if (mode === "reset") {
      if (!phone || !pin || !newPassword) { setError("All fields required"); return; }
      if (newPassword.length < 4) { setError("Password must be at least 4 characters"); return; }
    }

    setLoading(true);
    try {
      let res: Response;
      if (mode === "forgot") {
        res = await fetch(`${apiUrl}/api/forgot-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone }) });
        const data = await res.json();
        if (!res.ok) { setError(data.message); setLoading(false); return; }
        setSuccess(data.message);
        setMode("reset");
        setLoading(false);
        return;
      } else if (mode === "reset") {
        res = await fetch(`${apiUrl}/api/reset-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone, pin, newPassword }) });
        const data = await res.json();
        if (!res.ok) { setError(data.message); setLoading(false); return; }
        setSuccess("Password reset successfully! Please login.");
        setMode("login");
        resetForm();
        setLoading(false);
        return;
      } else if (mode === "register") {
        res = await fetch(`${apiUrl}/api/register`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone, password, nickname: nickname || undefined }) });
      } else {
        res = await fetch(`${apiUrl}/api/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone, password }) });
      }
      const data = await res.json();
      if (!res.ok) { setError(data.message || "Error occurred"); setLoading(false); return; }
      localStorage.setItem("aviator-token", data.token);
      localStorage.setItem("aviator-username", data.username);
      localStorage.setItem("aviator-balance", String(data.balance));
      if (rememberMe) {
        localStorage.setItem("aviator-saved-phone", phone);
        localStorage.setItem("aviator-saved-password", password);
      } else {
        localStorage.removeItem("aviator-saved-phone");
        localStorage.removeItem("aviator-saved-password");
      }
      updateUserInfo({ userName: data.username, balance: data.balance, token: data.token, currency: "KSh", phone: data.phone || phone });
      setShow(false);
      resetForm();
    } catch (e) { setError("Connection error. Please try again."); }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("aviator-token");
    localStorage.removeItem("aviator-username");
    localStorage.removeItem("aviator-balance");
    updateUserInfo({ userName: "", balance: 0, token: "", currency: "KSh" });
  };

  const inp: React.CSSProperties = { width:'100%', padding:'10px 12px', background:'#2a2a3e', border:'1px solid #444', borderRadius:'6px', color:'#fff', boxSizing:'border-box', fontSize:'14px', outline:'none', marginBottom:'12px' };
  const lbl: React.CSSProperties = { color:'#aaa', fontSize:'12px', display:'block', marginBottom:'4px' };

  if (userInfo?.token) {
    return (
      <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
        <span style={{color:'#aaa',fontSize:'11px'}}>👤 <span className='username-text'>{userInfo.userName}</span></span>
        <button onClick={handleLogout} title='Logout' style={{background:'transparent',color:'#ff4444',border:'1.5px solid #ff4444',padding:'0',borderRadius:'50%',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',width:'30px',height:'30px',flexShrink:0}}><MdPowerSettingsNew size={16}/></button>
      </div>
    );
  }

  return (
    <>
      <div style={{display:'flex',gap:'6px'}}>
        <button onClick={() => { setMode("login"); setShow(true); resetForm(); }}
          style={{background:'#333',color:'#fff',border:'1px solid #555',padding:'5px 12px',borderRadius:'4px',cursor:'pointer',fontSize:'12px',fontWeight:'bold'}}>
          Login
        </button>
        <button onClick={() => { setMode("register"); setShow(true); resetForm(); }}
          style={{background:'#ff4500',color:'#fff',border:'none',padding:'5px 12px',borderRadius:'4px',cursor:'pointer',fontSize:'12px',fontWeight:'bold'}}>
          Register
        </button>
      </div>

      {show && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#1a1a2e',border:'1px solid #333',borderRadius:'12px',padding:'28px',width:'360px',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.6)'}}>

            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px'}}>
              <h3 style={{color:'#fff',margin:0,fontSize:'18px'}}>
                {mode==="login"?"Login":mode==="register"?"Create Account":mode==="forgot"?"Forgot Password":"Reset Password"}
              </h3>
              <button onClick={() => { setShow(false); resetForm(); }} style={{background:'none',border:'none',color:'#aaa',fontSize:'24px',cursor:'pointer'}}>×</button>
            </div>

            {(mode==="login"||mode==="register") && (
              <div style={{display:'flex',gap:'8px',marginBottom:'20px'}}>
                <button onClick={() => { setMode("login"); setError(""); }}
                  style={{flex:1,padding:'8px',background:mode==="login"?'#ff4500':'#2a2a3e',color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:mode==="login"?'bold':'normal'}}>Login</button>
                <button onClick={() => { setMode("register"); setError(""); }}
                  style={{flex:1,padding:'8px',background:mode==="register"?'#ff4500':'#2a2a3e',color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:mode==="register"?'bold':'normal'}}>Register</button>
              </div>
            )}

            <label style={lbl}>Phone Number</label>
            <input placeholder="e.g. 0712345678" value={phone} onChange={e => setPhone(e.target.value)} type="tel" style={inp} />

            {mode==="register" && (
              <>
                <label style={lbl}>Nickname <span style={{color:'#555'}}>(optional — shown in bets)</span></label>
                <div style={{position:'relative',marginBottom:'12px'}}>
                  <input placeholder="e.g. AviatorKing" value={nickname} onChange={e => setNickname(e.target.value.replace(/\s/g,''))}
                    style={{...inp, marginBottom:0, paddingRight:'100px'}} />
                  {nicknameStatus!=="idle" && (
                    <span style={{position:'absolute',right:'10px',top:'10px',fontSize:'11px',
                      color:nicknameStatus==="available"?'#4caf50':nicknameStatus==="taken"?'#ff4444':'#aaa'}}>
                      {nicknameStatus==="checking"?"Checking...":nicknameStatus==="available"?"✓ Available":"✗ Taken"}
                    </span>
                  )}
                </div>
              </>
            )}

            {mode==="reset" && (
              <>
                <label style={lbl}>Reset PIN (check your phone)</label>
                <input placeholder="4-digit PIN" value={pin} onChange={e => setPin(e.target.value)} type="text" maxLength={4} style={inp} />
              </>
            )}

            {(mode==="login"||mode==="register") && (
              <>
                <label style={lbl}>Password</label>
                <div style={{position:'relative',marginBottom:'12px'}}>
                  <input placeholder="Enter password" type={showPassword?"text":"password"} value={password} onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => e.key==='Enter' && handleSubmit()} style={{...inp, marginBottom:0, paddingRight:'44px'}} />
                  <button type="button" onClick={() => setShowPassword(p => !p)}
                    style={{position:'absolute',right:'10px',top:'8px',background:'none',border:'none',color:'#aaa',cursor:'pointer',fontSize:'16px'}}>
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                {mode==="login" && (
                  <div style={{display:'flex',alignItems:'center',gap:'6px',marginBottom:'12px'}}>
                    <input type="checkbox" id="rememberMe" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}
                      style={{cursor:'pointer',accentColor:'#ff4500'}} />
                    <label htmlFor="rememberMe" style={{color:'#aaa',fontSize:'12px',cursor:'pointer'}}>Remember me</label>
                  </div>
                )}
              </>
            )}

            {mode==="reset" && (
              <>
                <label style={lbl}>New Password</label>
                <input placeholder="New password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={inp} />
              </>
            )}

            {mode==="register" && (
              <>
                <label style={lbl}>Confirm Password</label>
                <input placeholder="Repeat your password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={inp} />
              </>
            )}

            {error && <div style={{color:'#ff4444',fontSize:'12px',marginBottom:'12px',padding:'8px',background:'rgba(255,68,68,0.1)',borderRadius:'4px'}}>{error}</div>}
            {success && <div style={{color:'#4caf50',fontSize:'12px',marginBottom:'12px',padding:'8px',background:'rgba(76,175,80,0.1)',borderRadius:'4px'}}>{success}</div>}

            <button onClick={handleSubmit} disabled={loading}
              style={{width:'100%',padding:'12px',background:loading?'#555':'#ff4500',color:'#fff',border:'none',borderRadius:'6px',cursor:loading?'not-allowed':'pointer',fontWeight:'bold',fontSize:'15px',marginBottom:'12px'}}>
              {loading?"Please wait...":mode==="login"?"Login":mode==="register"?"Create Account":mode==="forgot"?"Send Reset PIN":"Reset Password"}
            </button>

            {mode==="login" && (
              <div style={{textAlign:'center'}}>
                <button onClick={() => { setMode("forgot"); setError(""); setSuccess(""); }}
                  style={{background:'none',border:'none',color:'#ff6a00',fontSize:'12px',cursor:'pointer',textDecoration:'underline'}}>
                  Forgot Password?
                </button>
              </div>
            )}

            {(mode==="forgot"||mode==="reset") && (
              <div style={{textAlign:'center'}}>
                <button onClick={() => { setMode("login"); setError(""); setSuccess(""); }}
                  style={{background:'none',border:'none',color:'#aaa',fontSize:'12px',cursor:'pointer',textDecoration:'underline'}}>
                  Back to Login
                </button>
              </div>
            )}

            {mode==="register" && (
              <p style={{color:'#666',fontSize:'11px',textAlign:'center',marginTop:'12px',marginBottom:0}}>
                📱 Your phone number will be used for M-Pesa deposits
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default function Header() {
  const { userInfo, updateUserInfo, onlineCount, toggleMsgTab, msgTab } = React.useContext(Context);
  const [howto, setHowto] = React.useState<'howto'|'short'|'more'|''>('howto');
  const [, setFireSystem] = React.useState(false);
  const [soundOn, setSoundOn] = React.useState(localStorage.getItem("aviator-sound") !== "false");
  const [walletOpen, setWalletOpen] = React.useState(false);
  const [showMyAccount, setShowMyAccount] = React.useState(false);
  const [walletTab, setWalletTab] = React.useState<'deposit'|'withdraw'|'history'>('deposit');
  const [walletAmount, setWalletAmount] = React.useState('');
  const [walletPhone, setWalletPhone] = React.useState(userInfo.phone || '');
  const [walletMsg, setWalletMsg] = React.useState('');
  const [walletLoading, setWalletLoading] = React.useState(false);
  const [walletPending, setWalletPending] = React.useState('');
  const [txHistory, setTxHistory] = React.useState<any[]>([]);
  React.useEffect(() => {
    if (!walletOpen) return;
    if (userInfo.phone) { const p = userInfo.phone; setWalletPhone(p.startsWith("254") ? "0" + p.slice(3) : p); }
    if (userInfo.token) {
      fetch('/api/my-history', { headers: { Authorization: 'Bearer ' + userInfo.token } })
        .then(r => r.json()).then(d => { if (d.status) setTxHistory(d.data || []); }).catch(() => {});
    }
  }, [walletOpen]);

  const handleDeposit = async () => {
    setWalletMsg('');
    if (!walletAmount || parseFloat(walletAmount) < 10) { setWalletMsg('Minimum deposit is KSh 10'); return; }
    if (!walletPhone) { setWalletMsg('Enter your M-Pesa phone number'); return; }
    setWalletLoading(true);
    try {
      const res = await fetch('/api/mpesa/deposit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + userInfo.token },
        body: JSON.stringify({ phone: walletPhone, amount: parseFloat(walletAmount) })
      });
      const data = await res.json();
      if (data.checkoutRequestId) {
        setWalletMsg('📲 STK push sent! Enter your M-Pesa PIN on your phone.');
        setWalletPending(data.checkoutRequestId);
        let attempts = 0;
        const interval = setInterval(async () => {
          attempts++;
          try {
            const r = await fetch('/api/mpesa/status/' + data.checkoutRequestId, {
              headers: { 'Authorization': 'Bearer ' + userInfo.token }
            });
            const d = await r.json();
            if (d.txStatus === 'completed') {
              clearInterval(interval);
              setWalletPending('');
              updateUserInfo({ ...userInfo, balance: userInfo.balance + parseFloat(walletAmount) });
              setWalletMsg('✅ KSh ' + walletAmount + ' deposited successfully!');
              setWalletAmount('');
              setTimeout(() => { setWalletOpen(false); setWalletMsg(''); }, 2500);
            } else if (d.txStatus === 'failed') {
              clearInterval(interval);
              setWalletPending('');
              setWalletMsg('❌ ' + (d.message || 'Payment failed. Try again.'));
            } else if (attempts >= 24) {
              clearInterval(interval);
              setWalletPending('');
              setWalletMsg('⏱️ Payment timed out. If deducted, contact support.');
            } else {
              setWalletMsg('⏳ Confirming payment... (' + (attempts * 5) + 's)');
            }
          } catch { /* keep polling */ }
        }, 5000);
      } else {
        setWalletMsg('❌ ' + (data.error || data.message || 'Deposit failed. Try again.'));
      }
    } catch { setWalletMsg('❌ Network error. Check connection and try again.'); }
    setWalletLoading(false);
  };

  const handleWithdraw = async () => {
    setWalletMsg('');
    if (!walletAmount || parseFloat(walletAmount) < 50) { setWalletMsg('Minimum withdrawal is KSh 50'); return; }
    if (!walletPhone) { setWalletMsg('Enter your M-Pesa phone number'); return; }
    if (parseFloat(walletAmount) > userInfo.balance) { setWalletMsg('Insufficient balance'); return; }
    setWalletLoading(true);
    try {
      const res = await fetch('/api/withdraw', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + userInfo.token },
        body: JSON.stringify({ phone: walletPhone, amount: parseFloat(walletAmount) })
      });
      const data = await res.json();
      if (data.withdrawalId || data.status) {
        setWalletMsg('✅ Withdrawal submitted! You will receive your funds within 24 hours.');
        updateUserInfo({ ...userInfo, balance: userInfo.balance - parseFloat(walletAmount) });
        setWalletAmount('');
      } else { setWalletMsg(data.message || 'Withdrawal failed'); }
    } catch { setWalletMsg('Network error. Try again.'); }
    setWalletLoading(false);
  };

  return (
    <div className="header flex-none items-center">
      <div className="header-container">
        <div className="logo-container">
          <img src={logo} alt="logo" className="logo"></img>
        </div>
        <div className="second-block" style={{display:'flex',alignItems:'center',gap:'10px'}}>
          <AuthModal />
          <button className="howto" onClick={() => setHowto("short")}>
            <div className="help-logo"></div>
            <div className="help-msg">How to play ?</div>
          </button>
          <div className="d-flex" style={{display:'flex',alignItems:'center',gap:'8px'}}>
            <div className="balance">
              <span className="amount">{Number(userInfo.balance).toFixed(2)} </span>
              <span className="currency">&nbsp;KSh</span>
            </div>
            <button onClick={toggleMsgTab}
              style={{background:msgTab?'#ff4500':'#2a2a3e',border:msgTab?'none':'1px solid #444',color:'#fff',padding:'5px 10px',borderRadius:'6px',cursor:'pointer',fontSize:'14px',position:'relative'}}
              title="Chat">
              💬
            </button>
            <button onClick={() => {
              const current = localStorage.getItem("aviator-sound") !== "false";
              localStorage.setItem("aviator-sound", current ? "false" : "true");
              setSoundOn(!current);
            }}
              style={{background:'#2a2a3e',border:'1px solid #444',color:'#fff',padding:'5px 10px',borderRadius:'6px',cursor:'pointer',fontSize:'14px'}}>
              {soundOn ? '🔊' : '🔇'}
            </button>
            {onlineCount > 0 && <span style={{color:'#22c55e',fontSize:'11px',display:'flex',alignItems:'center',gap:'4px'}}><span style={{width:'6px',height:'6px',borderRadius:'50%',background:'#22c55e',display:'inline-block'}}/>{onlineCount} online</span>}
            {userInfo.token && (
              <>
                <button onClick={() => setWalletOpen(true)}
                  style={{background:'#ff4500',border:'none',color:'#fff',padding:'5px 12px',borderRadius:'6px',cursor:'pointer',fontWeight:'bold',fontSize:'13px'}}>
                  + Wallet
                </button>
                <button onClick={() => setShowMyAccount(true)}
                  style={{width:'32px',height:'32px',borderRadius:'50%',background:'linear-gradient(135deg,#ff4500,#ff6a00)',border:'none',color:'#fff',cursor:'pointer',fontWeight:'bold',fontSize:'14px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  {(userInfo.userName||'P').slice(0,1).toUpperCase()}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {howto==="short" && (
        <div className="modal">
          <div className="back" onClick={() => setHowto("howto")}></div>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header modal-bg text-uppercase">
                <span>How to Play?</span>
                <button onClick={() => setHowto('')} className="close modal-close"><span>×</span></button>
              </div>
              <div className="modal-body m-body-bg">
                <div className="step"><div className="bullet">01</div><p>Place a bet and wait for the round to start.</p></div>
                <div className="step"><div className="bullet bullet-2">02</div><p>Watch the lucky plane take off. Cash out before it flies away!</p></div>
                <div className="step"><div className="bullet bullet-3">03</div><p>Your win = bet × multiplier at cash out time.</p></div>
              </div>
              <div className="modal-footer m-f-bg">
                <button onClick={() => setHowto("more")}>Detailed Rules</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {howto==="more" && (
        <div className="modal">
          <div className="back" onClick={() => setHowto('')}></div>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header modal-bg text-uppercase">
                <span>Detailed Rules</span>
                <button onClick={() => setHowto('')} className="close modal-close"><span>×</span></button>
              </div>
              <div className="modal-body m-body-bg">
                <div className="rules-list pt-2">
                  <div className="rules-list-title">Game Rules</div>
                  <ul className="list-group">
                    <li className="list-group-item">The multiplier starts at 1x and increases until the plane flies away.</li>
                    <li className="list-group-item">You must cash out before the plane flies away to win.</li>
                    <li className="list-group-item">If you do not cash out before the plane flies away, you lose your bet.</li>
                    <li className="list-group-item">You can place up to 2 bets per round.</li>
                    <li className="list-group-item">Auto cash out is available — set your target multiplier in advance.</li>
                  </ul>
                </div>
                <div className="rules-list pt-2">
                  <div className="rules-list-title">Fairness</div>
                  <ul className="list-group">
                    <li className="list-group-item">Each round result is provably fair and determined before the round starts.</li>
                    <li className="list-group-item">The house edge is 5%. This means the game pays out 95% of all bets over time.</li>
                  </ul>
                </div>
                <div className="rules-list pt-2">
                  <div className="rules-list-title">Other</div>
                  <ul className="list-group">
                    <li className="list-group-item">If your connection drops while a bet is active, the game will auto cash out at the current multiplier.</li>
                    <li className="list-group-item">In the event of a malfunction, all affected bets are refunded.</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer m-f-bg">
                <button onClick={() => setHowto("short")}>Back</button>
              </div>
            </div>
          </div>
        </div>
      )}
    {walletOpen && userInfo.token && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
          <div style={{background:'#1a1a2e',borderRadius:'12px',width:'340px',padding:'24px',position:'relative'}}>
            <button onClick={() => { setWalletOpen(false); setWalletMsg(''); setWalletAmount(''); }}
              style={{position:'absolute',top:'12px',right:'12px',background:'none',border:'none',color:'#aaa',fontSize:'20px',cursor:'pointer'}}>×</button>
            <div style={{color:'#ff4500',fontWeight:'bold',fontSize:'18px',marginBottom:'4px'}}>💰 Wallet</div>
            <div style={{color:'#aaa',fontSize:'12px',marginBottom:'16px'}}>Balance: <span style={{color:'#4caf50',fontWeight:'bold'}}>KSh {Number(userInfo.balance).toFixed(2)}</span></div>
            <div style={{display:'flex',gap:'8px',marginBottom:'20px'}}>
              <button onClick={() => { setWalletTab('deposit'); setWalletMsg(''); }}
                style={{flex:1,padding:'8px',borderRadius:'6px',border:'none',cursor:'pointer',fontWeight:'bold',fontSize:'13px',
                  background: walletTab==='deposit' ? '#ff4500' : '#2a2a3e', color: walletTab==='deposit' ? '#fff' : '#aaa'}}>
                Deposit
              </button>
              <button onClick={() => { setWalletTab('withdraw'); setWalletMsg(''); }}
                style={{flex:1,padding:'8px',borderRadius:'6px',border:'none',cursor:'pointer',fontWeight:'bold',fontSize:'13px',
                  background: walletTab==='withdraw' ? '#ff4500' : '#2a2a3e', color: walletTab==='withdraw' ? '#fff' : '#aaa'}}>
                Withdraw
              </button>
              <button onClick={() => { setWalletTab('history'); setWalletMsg(''); }}
                style={{flex:1,padding:'8px',borderRadius:'6px',border:'none',cursor:'pointer',fontWeight:'bold',fontSize:'13px',
                  background: walletTab==='history' ? '#ff4500' : '#2a2a3e', color: walletTab==='history' ? '#fff' : '#aaa'}}>
                History
              </button>
            </div>
            <div style={{marginBottom:'12px'}}>
              <label style={{color:'#aaa',fontSize:'12px',display:'block',marginBottom:'4px'}}>M-Pesa Phone Number</label>
              <input type="tel" placeholder="e.g. 0712345678" value={walletPhone}
                onChange={e => setWalletPhone(e.target.value)}
                style={{width:'100%',background:'#0d0d1a',border:'1px solid #333',color:'#fff',padding:'8px 12px',borderRadius:'6px',fontSize:'14px',boxSizing:'border-box'}} />
            </div>
            <div style={{marginBottom:'16px'}}>
              <label style={{color:'#aaa',fontSize:'12px',display:'block',marginBottom:'4px'}}>
                Amount (KSh) — {walletTab==='deposit' ? 'Min: 10' : 'Min: 50'}
              </label>
              <input type="number" placeholder="Enter amount" value={walletAmount}
                onChange={e => setWalletAmount(e.target.value)}
                style={{width:'100%',background:'#0d0d1a',border:'1px solid #333',color:'#fff',padding:'8px 12px',borderRadius:'6px',fontSize:'14px',boxSizing:'border-box'}} />
              <div style={{display:'flex',gap:'6px',marginTop:'8px'}}>
                {[100,200,500,1000].map(amt => (
                  <button key={amt} onClick={() => setWalletAmount(String(amt))}
                    style={{flex:1,padding:'5px',background:'#2a2a3e',border:'none',color:'#aaa',borderRadius:'4px',cursor:'pointer',fontSize:'11px'}}>
                    {amt}
                  </button>
                ))}
              </div>
            </div>
            {walletMsg && (
              <div style={{background: walletMsg.includes('confirmed') || walletMsg.includes('sent') ? '#1a3a1a' : '#3a1a1a',
                border: `1px solid ${walletMsg.includes('confirmed') || walletMsg.includes('sent') ? '#4caf50' : '#ff4444'}`,
                color: walletMsg.includes('confirmed') || walletMsg.includes('sent') ? '#4caf50' : '#ff6666',
                padding:'8px 12px',borderRadius:'6px',fontSize:'12px',marginBottom:'12px'}}>
                {walletMsg}
              </div>
            )}
            {walletPending && (
              <div style={{color:'#ffaa00',fontSize:'12px',marginBottom:'12px',textAlign:'center'}}>
                ⏳ Waiting for payment confirmation...
              </div>
            )}
            {(walletTab==='deposit'||walletTab==='withdraw') && (
              <button onClick={walletTab==='deposit' ? handleDeposit : handleWithdraw}
                disabled={walletLoading}
                style={{width:'100%',padding:'11px',background:walletLoading?'#555':'#ff4500',border:'none',color:'#fff',borderRadius:'6px',fontWeight:'bold',fontSize:'15px',cursor:walletLoading?'not-allowed':'pointer'}}>
                {walletLoading ? 'Processing...' : walletTab==='deposit' ? 'Deposit via M-Pesa' : 'Withdraw to M-Pesa'}
              </button>
            )}
            {walletTab==='history' && (
              <div style={{maxHeight:'320px',overflowY:'auto'}}>
                {txHistory.length===0 ? (
                  <div style={{textAlign:'center',color:'#555',padding:'40px 0'}}>
                    <div style={{fontSize:'32px',marginBottom:'8px'}}>📋</div>No transactions yet
                  </div>
                ) : txHistory.map((w:any,i:number) => (
                  <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',borderBottom:'1px solid #1a1a3e',padding:'10px 0'}}>
                    <div>
                      <div style={{color:'#fff',fontWeight:'bold',fontSize:'14px'}}>KSh {Number(w.amount).toLocaleString()}</div>
                      <div style={{color:'#555',fontSize:'10px',marginTop:'2px'}}>{w.reference}</div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontSize:'11px',fontWeight:'bold',color:w.status==='approved'?'#22c55e':w.status==='rejected'?'#ef4444':'#facc15'}}>{(w.status||'').toUpperCase()}</div>
                      <div style={{color:'#444',fontSize:'10px',marginTop:'2px'}}>{new Date(w.createdAt).toLocaleDateString('en-KE')}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    {showMyAccount && userInfo.token && <MyAccount onClose={() => setShowMyAccount(false)} />}
    </div>
  );
}
